import GhostDescription from '@common/GhostDescription';
import { useUserTeams } from '@common/userHooks';
import { theme } from '@prophecy/theme';
import { Controller, GhostInput, Spinner, Stack } from '@prophecy/ui-v3';
import { useValidForm } from '@prophecy/ui-v3/Form/useValidForm';
import { StyledInputContainer } from '@prophecy/ui-v3/Input/styled';
import { tokens } from '@prophecy/ui-v3/Input/tokens';
import { validateEntityName } from '@prophecy/utils/validation';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { TwoThirdStackitem } from '../styles';
import { FabricTokens } from '../tokens';
import { BasicFormData } from '../types';
import { TeamSelector } from './TeamSelector';

const FullWidthTeams = styled(TeamSelector)`
  width: 100%;
`;

const StyledGhostInput = styled(GhostInput)`
  ${StyledInputContainer} {
    &[disabled],
    input[disabled] {
      background-color: ${({ variant }) => tokens.Input[variant || 'primary'].backgroundColor} !important;
    }
  }
`;

const BasicForm = ({
  fabricId,
  formContext,
  updateFabricErrors
}: {
  fabricId?: string;
  formContext: UseFormReturn<BasicFormData>;
  updateFabricErrors: (e: boolean) => void;
}) => {
  const { isInitialLoading: isLoading } = useUserTeams();

  const { control } = formContext;

  const { hasErrors } = useValidForm(formContext);

  useEffect(() => {
    updateFabricErrors(hasErrors);
  }, [updateFabricErrors, hasErrors]);

  return (
    <>
      {isLoading ? (
        <Spinner spinning />
      ) : (
        <Stack
          align='start'
          gap={FabricTokens.Form.fieldGapXL}
          direction='vertical'
          padding={`${theme.spaces.x24} 0 0 0`}>
          <Stack align='start' gap={theme.spaces.x24} direction='vertical'>
            <Controller
              render={({ field }) => (
                <StyledGhostInput
                  placeholder='Enter fabric name'
                  inputSize='l'
                  {...field}
                  disabled={Boolean(fabricId)}
                  data-test-id='fabric-basic-form-name-input'
                />
              )}
              control={control}
              name='name'
              rules={{
                required: 'Name is required',
                validate: validateEntityName
              }}
            />
            <Controller
              render={({ field }) => (
                <GhostDescription
                  value={field.value as string}
                  onChange={field.onChange}
                  disabled={Boolean(fabricId)}
                  data-test-id='fabric-basic-form-description-textarea'
                />
              )}
              control={control}
              name='description'
            />
          </Stack>

          <TwoThirdStackitem>
            <Controller
              render={({ field }) => (
                <FullWidthTeams
                  {...field}
                  label='Team'
                  disabled={Boolean(fabricId)}
                  data-test-id='fabric-basic-team-select'
                />
              )}
              control={control}
              name='ownerUID'
              rules={{
                required: 'Team is required'
              }}
            />
          </TwoThirdStackitem>
        </Stack>
      )}
    </>
  );
};

export default BasicForm;
